"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";

import { useAuth } from "@/context/auth-context";

interface TutorRedirectGuardProps {
  children: React.ReactNode;
}

export function TutorRedirectGuard({ children }: TutorRedirectGuardProps) {
  const { user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (user && user.role?.toLowerCase() === "tutor") {
      const onboardingStatus = (user as any).onboardingStatus?.toUpperCase?.();
      const emailVerified = (user as any).emailVerified;
      
      // Public pages that are always accessible
      // Must stay in sync with middleware's tutorPublicPages list
      const publicPages = [
        "/forums",
        "/feed",
        "/tutors",
        "/about",
        "/help",
        "/resources",
        "/contact",
        "/privacy",
        "/terms",
        "/delete-account",
        "/account-deleted",
        "/social-profile",
        "/notifications",
        "/find-people",
      ];
      
      // Pages that unapproved tutors should be allowed to access
      const allowedPages = [
        "/tutor/onboarding",
        "/tutor/setup",
        "/tutor/rejected",
        "/api/tutors/onboarding",
        "/",
        "/about",
        "/help",
        "/resources",
        "/contact",
        "/privacy",
        "/terms",
        "/delete-account",
        "/account-deleted",
        "/logout",
        "/login",
        "/register",
        "/verify-email",
        "/forgot-password",
        "/reset-password",
      ];

      const isPublicPage = publicPages.some(page => pathname.startsWith(page));
      const isAllowedPage = allowedPages.some(page => pathname.startsWith(page));
      
      // If email is not verified, only allow access to verification page and public pages
      if (!emailVerified && !pathname.startsWith("/verify-email") && !isPublicPage) {
        router.push("/verify-email");
        return;
      }

      // If tutor has no onboarding status or is not approved, redirect to onboarding (case-insensitive)
      if (!onboardingStatus || ["PENDING", "IN_PROGRESS", "SUBMITTED", "UNDER_REVIEW"].includes(onboardingStatus)) {
        if (!isAllowedPage && !isPublicPage) {
          router.push("/tutor/onboarding");
          return;
        }
      }
      
      // If tutor is rejected, redirect to rejection page
      if (onboardingStatus === "REJECTED" && !pathname.startsWith("/tutor/rejected") && !isPublicPage) {
        router.push("/tutor/rejected");
        return;
      }
    }
  }, [user, router, pathname]);

  return <>{children}</>;
} 